import React, { useEffect, useState } from "react";
import { API_URL } from "src/scenes/App";
import axios from "axios"; 
import { Text, Form, BusinessPortalHeader,
    PortalPageShell, Toast, PortalFormActions,
    PortalFormScrollBody, PortalFormSection, PortalFormSectionDescription,
    PortalFormSectionHeader, PortalFormSectionTitle, PortalFormSurface,
    StandardModalActionButton, StandardModalActionGroup
} from "src/components";

import { StyledButton, FormBackground } from "../components/Utils";
import { EnumSurveyShowFrequency, ErrorList,
    FormStep, EnumUserGroup, getAvailableSurveyShowInOptions
} from "../components/Enums";

import { SurveyNameField } from "./components/SurveysComponents/SurveyNameField";
import { UserGroupField } from "./components/SurveysComponents/UserGroupField";
import { SurveyQuestionsFields } from "./components/SurveysComponents/SurveyQuestionsFields";
import { SurveySubmissionAnonymousField } from "./components/SurveysComponents/SurveySubmissionAnonymousField";
import { SurveyFrequencyField } from "./components/SurveysComponents/SurveyFrequencyField";
import { SurveyDurationFields } from "./components/SurveysComponents/SurveyDurationFields";
import { TypeOfSurveyField } from "./components/SurveysComponents/TypeOfSurveyField";
import ImageUpload from "./components/PollsImageUpload";
import { getDefaultStartScreen } from "src/scenes/Customise/components/ScreenUtils";
import { UserKeys } from "src/constants/userDetails";
import { EnumSurveyType } from "../components/Enums";
import { buildSurveyUpdatePayload } from "./surveyUpdatePayload";

const SurveyForm = (props) => {
    const { user, surveyToEdit, showSurveyForm, getSurveys, selectedSurveyOrPollStatusType } = props;
    const permissions = user?.[UserKeys.PERMISSIONS];
    const defaultScreen = getDefaultStartScreen(permissions);
    const availableShowInOptions = getAvailableSurveyShowInOptions(user?.featureFlags);
    const defaultShowIn = availableShowInOptions[0]?.value || defaultScreen;
    const [survey, setSurvey] = useState({
        name: "",
        surveyType: "",
        startDate: "",
        finishDate: "",
        userGroup: "",
        surveyQuestions: [
            { uuid: "", question: "", questionOrder: 1, surveyType: "" }
        ],
        showIn: defaultShowIn,
        showFrequency: EnumSurveyShowFrequency.EVERYTIME,
        imageUrl: "",
        isAnonymous: false,
        status: ""
    });
    const [inputErrors, setInputErrors] = useState([]);
    const [formStep, setFormStep] = useState(FormStep.PAGE_SURVEY_NAME);
    const [setInputTypeAs] = useState("text");
    const [formSubmitLoading, setFormSubmitLoading] = useState(false);
    const [isEditSurveyForm, setIsEditSurveyForm] = useState(false);
    const [showPollVisibilityModal, setShowPollVisibilityModal] = useState(false);
    const [allEmployeesInSurvey, setAllEmployeesInSurvey] = useState(false);
    const [allClientsInSurvey, setAllClientsInSurvey] = useState(false);
    const [employeesCountInPoll, setEmployeesCountInPoll] = useState(0);
    const [clientsCountInPoll, setClientsCountInPoll] = useState(0);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [isShowInitialUserGroupAddButton, setIsShowInitialUserGroupAddButton] = useState(true);
    const [formImage, setFormImage] = useState(null);
    const [imageChanged, setImageChanged] = useState(false);

    useEffect(() => {
        if (surveyToEdit) {
            setSurveyToEdit(surveyToEdit);
            setIsShowInitialUserGroupAddButton(false);
            setSelectedUsers(surveyToEdit.usersSelectedUuid);
            setAllEmployeesInSurvey(surveyToEdit.employeeCount === surveyToEdit.totalEmployeeCount);
            setAllClientsInSurvey(surveyToEdit.clientCount > 0 && surveyToEdit.clientCount === surveyToEdit.totalClientCount);
            setIsEditSurveyForm(true);
        } else {
            setIsEditSurveyForm(false);
        }
    }, [surveyToEdit]);

    useEffect(() => {
        if (!surveyToEdit && !survey.showIn && defaultShowIn) {
            updateSurvey("showIn", defaultShowIn);
        }
    }, [defaultShowIn, survey.showIn, surveyToEdit]);

    const setSurveyToEdit = (surveyToEdit) => {
        const surveyImageUrl = surveyToEdit.image?.isUsingCompanyLogo ? "" : surveyToEdit.image?.imageUrl || "";

        setSurvey({
            name: surveyToEdit.name || "",
            surveyType: surveyToEdit.surveyType || "",
            startDate: surveyToEdit.startDate || "",
            finishDate: surveyToEdit.finishDate || "",
            userGroup: surveyToEdit.userGroup || "",
            surveyQuestions: surveyToEdit.surveyQuestions,
            showIn: surveyToEdit.showIn || defaultShowIn,
            showFrequency: surveyToEdit.showFrequency || EnumSurveyShowFrequency.EVERYTIME,
            imageUrl: surveyImageUrl,
            isAnonymous: surveyToEdit.isAnonymous || false,
            status: surveyToEdit.status || "",
            clientCount: surveyToEdit.clientCount,
            employeeCount: surveyToEdit.employeeCount,
            totalEmployeeCount: surveyToEdit.totalEmployeeCount,
            totalClientCount: surveyToEdit.totalClientCount,
            usersSelectedUuid: surveyToEdit.usersSelectedUuid
        });
        setFormImage(surveyImageUrl || null);
        setImageChanged(false);
    };

    const updateSurvey = (key, value) => {
        setSurvey(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    const inputSurveyName = (event) => {
        updateSurvey("name", event.target.value);
    };

    const inputStartDate = (selectedDate) => {
        updateSurvey("startDate", selectedDate);
    };

    const inputFinishDate = (selectedDate) => {
        updateSurvey("finishDate", selectedDate);
    };

    const selectSurveyType = (event) => {
        setSurvey(prev => ({
            ...prev,
            surveyType: event.value,
            surveyQuestions: prev.surveyQuestions.map(question => ({
                ...question,
                surveyType: event.value === EnumSurveyType.MULTI_TYPE ? question.surveyType || "" : event.value
            }))
        }));
    };

    const inputQuestion = (questionNumber, event) => {
        if (questionNumber < 1) {
            console.warn("Survey Question items starts with 1");
            return;
        }

        const questionString = event.target.value;
        const questions = structuredClone(survey.surveyQuestions);
        questions[questionNumber - 1] = {
            ...questions[questionNumber - 1],
            question: questionString,
            questionOrder: questionNumber
        };
        setSurvey(prev => ({
            ...prev,
            surveyQuestions: questions
        }));
    };

    const selectQuestionSurveyType = (questionNumber, choice) => {
        const questions = structuredClone(survey.surveyQuestions);
        questions[questionNumber - 1] = {
            ...questions[questionNumber - 1],
            surveyType: choice.value,
            questionOrder: questionNumber
        };
        setSurvey(prev => ({
            ...prev,
            surveyQuestions: questions
        }));
    };

    const selectUserGroup = (event) => {
        updateSurvey("userGroup", event.value);
        setInputErrors([]); // reset errors
    };

    const removeQuestion = (questionNumber) => {
        if (questionNumber < 1 || questionNumber > survey.surveyQuestions.length) {
            return;
        }
        const questions = structuredClone(survey.surveyQuestions).toSpliced(questionNumber - 1, 1);
        setSurvey(prev => ({
            ...prev,
            surveyQuestions: questions
        }));
    };

    const addQuestion = () => {

        const questionOrder = survey.surveyQuestions.length + 1;
        const questions = structuredClone(survey.surveyQuestions);
        questions.push({
            uuid: "",
            question: "",
            questionOrder: questionOrder,
            surveyType: survey.surveyType === EnumSurveyType.MULTI_TYPE ? "" : survey.surveyType
        });
        setSurvey(prev => ({
            ...prev,
            surveyQuestions: questions
        }));
    };

    const selectShowIn = (event) => {
        updateSurvey("showIn", event.value);
    };

    const setSurveyAnonymous = (event) => {
        updateSurvey("isAnonymous", event);
    };

    const selectShowFrequency = (event) => {
        updateSurvey("showFrequency", event.value);
    };
    
    const validateSurvey = (survey) => {
        const errors = [];
    
        if (!survey.name) {
            errors.push(ErrorList.EMPTY_SURVEY_NAME);
        }
        if (!survey.surveyType) {
            errors.push(ErrorList.EMPTY_SURVEY_TYPE);
        }
        if (!survey.userGroup) {
            errors.push(ErrorList.EMPTY_USER_GROUP);
        }
        if (!survey.showIn) {
            errors.push(ErrorList.EMPTY_SHOW_IN);
        }
        if (!survey.showFrequency) {
            errors.push(ErrorList.EMPTY_SHOW_FREQUENCY);
        }
        if (survey.startDate && survey.finishDate && survey.startDate > survey.finishDate) {
            errors.push(ErrorList.DATE_RANGE_INVALID);
        }
        if (!survey.startDate) {
            errors.push(ErrorList.EMPTY_START_DATE);
        } else {
            if (isNaN(new Date(survey.startDate))) {
                errors.push(ErrorList.DATE_START_DATE_INVALID);
            }
        }
        if (!survey.finishDate) {
            errors.push(ErrorList.EMPTY_FINISH_DATE);
        } else {
            if (isNaN(new Date(survey.finishDate))) {
                errors.push(ErrorList.DATE_FINISH_DATE_INVALID);
            }
        }
        if (survey.userGroup === EnumUserGroup.SELECTED_USERS && selectedUsers.length === 0) {
            errors.push(ErrorList.EMPTY_SELECTED_USERS);
        }

        if (survey.surveyQuestions.length < 1) {
            errors.push(ErrorList.QUESTION_MIN_REQUIRED);
        }

        survey.surveyQuestions.forEach((surveyQuestion, index) => {
            if (surveyQuestion.question.length === 0) {
                errors.push({
                    type: ErrorList.EMPTY_QUESTION,
                    index: index,
                });
            }
            if (survey.surveyType === EnumSurveyType.MULTI_TYPE && !surveyQuestion.surveyType) {
                errors.push({
                    type: ErrorList.EMPTY_QUESTION_SURVEY_TYPE,
                    index: index,
                });
            }
        });

        return errors;
    };

    const uploadSurveyImage = async () => {
        if (!formImage || !imageChanged) {
            return typeof formImage === "string" ? formImage : "";
        }

        if (!(formImage instanceof File)) {
            return "";
        }

        const imageType = formImage.type.replace("image/", "");
        const presignedResponse = await axios.get(`${API_URL}/survey/company/generateImageUrl/${imageType}`, {
            headers: { Authorization: `Bearer ${user.token}` }
        });
        const presignedUrl = presignedResponse.data.url;
        await axios.put(presignedUrl, formImage, {
            headers: { "Content-Type": formImage.type }
        });

        const queryIndex = presignedUrl.indexOf("?");
        return queryIndex === -1 ? presignedUrl : presignedUrl.substring(0, queryIndex);
    };
    
    const createFormSubmission = async () => {
        
        setInputErrors([]);
        setFormSubmitLoading(true);
    
        const errors = validateSurvey(survey);
        if (errors.length > 0) {
            setInputErrors(errors);
            setFormSubmitLoading(false);
            return;
        }

        let uploadedImageUrl = "";
        try {
            uploadedImageUrl = await uploadSurveyImage();
        } catch (error) {
            console.log(error)
            setFormSubmitLoading(false);
            return Toast.error("Something went wrong while uploading the survey image.");
        }
    
        // Format dates for the request
        const formatter = Intl.DateTimeFormat("fr-CA", { year: "numeric", month: "2-digit", day: "2-digit" });
        const formattedStartDate = formatter.format(new Date(survey.startDate)) + " 00:00:00";
        const formattedFinishDate = survey.finishDate ? formatter.format(new Date(survey.finishDate)) + " 23:59:59" : null;
    
        const surveyQuestions = Object.fromEntries(
            Object.entries(survey.surveyQuestions)
                .filter(([_, question]) => question.question)
        );
        let bodyObject = {};
        if (survey.userGroup === EnumUserGroup.SELECTED_USERS) {
            bodyObject = {
                ...survey,
                startDate: formattedStartDate,
                finishDate: formattedFinishDate,
                imageUrl: uploadedImageUrl,
                surveyQuestions,
                selectedUsers: selectedUsers
            };
        } else {
            bodyObject = {
                ...survey,
                startDate: formattedStartDate,
                finishDate: formattedFinishDate,
                imageUrl: uploadedImageUrl,
                surveyQuestions,
            };
        }
    
        // Axios request
        axios.post(`${API_URL}/survey/company`, bodyObject, {
            headers: { Authorization: `Bearer ${user.token}` }
        }).then(() => {
            setFormStep(FormStep.PAGE_CONGRATULATIONS);
        }).catch(error => {
            onSavingError(error);
        }).finally(() => setFormSubmitLoading(false));
    };

    const updateFormSubmission = async (surveyToEdit) => {
        
        setInputErrors([]);
        setFormSubmitLoading(true);

        let errors = validateSurvey(survey);
        
        if (errors.length > 0) {
            setFormSubmitLoading(false);
            setInputErrors(errors);
            return Toast.error("Please make sure your entries are correct");
        }

        let uploadedImageUrl = "";
        try {
            uploadedImageUrl = await uploadSurveyImage();
        } catch (error) {
            console.log(error)
            setFormSubmitLoading(false);
            return Toast.error("Something went wrong while uploading the survey image.");
        }

        const formattedPayload = buildSurveyUpdatePayload(
            survey,
            surveyToEdit,
            selectedUsers,
            uploadedImageUrl
        );

        axios.put(`${API_URL}/survey/company/${surveyToEdit.uuid}`, formattedPayload,
            {
                headers: {
                    Authorization: "Bearer " + user.token
                }
            }).then(res => {
            const surveyResponse = res.data.survey;
            setSurvey(surveyResponse);
        }).then(response => {
            getSurveys(selectedSurveyOrPollStatusType);
            showSurveyForm(false);
            return Toast.info("Survey updated successfully");
        }).catch(error => {
            onSavingError(error);
           
        }).finally(() => {
            setFormSubmitLoading(false);
        });
    };

    const onSavingError = (error) => {
        const formErrors = error.getFormErrors();
        if (error.hasFormErrors()) {
            const inputErrors = onCatchErrorFields(formErrors.map(({ field }) => field));
            setInputErrors(inputErrors);
            return Toast.error("Please make sure your entries are correct");
        }

        Toast.error(error.getApiErrorDetails().message);
    };

    const onCatchErrorFields = (errorFields) => {
        let errors = [];
        
        errorFields.forEach(function(fieldKey) {
            switch (fieldKey) {
                case "startDateInvalid":
                    errors.push(ErrorList.DATE_START_DATE_INVALID);
                    return;
                case "finishDateInvalid":
                    errors.push(ErrorList.DATE_FINISH_DATE_INVALID);
                    return;
                case "startDateGreaterThanFinishDate":
                    errors.push(ErrorList.DATE_RANGE_INVALID);
                    return;
                case "name":
                    errors.push(ErrorList.EMPTY_SURVEY_NAME);
                    return;
                case "surveyType":
                    errors.push(ErrorList.EMPTY_SURVEY_TYPE);
                    return;
                case "userGroup":
                    errors.push(ErrorList.EMPTY_USER_GROUP);
                    return;
                case "startDateEmpty":
                    errors.push(ErrorList.EMPTY_START_DATE);
                    break;
                case "finishDateEmpty":
                    errors.push(ErrorList.EMPTY_FINISH_DATE);
                    return;
                //case "questionCount": 
                    //serverErrors.push(ErrorList.QUESTION_LIMIT_REACHED);
                    //return;
                case "surveyQuestionEmpty":
                    errors.push(ErrorList.QUESTION_MIN_REQUIRED);
                    return;
                case "surveyQuestionImmutable":
                    errors.push(ErrorList.SURVEY_QUESTION_IMMUTABLE);
                    return;
                case "surveyQuestionType":
                    errors.push(ErrorList.EMPTY_QUESTION_SURVEY_TYPE);
                    return;
                default:
                    //NO ERROR TO CATCH
                    return;
            }
        });

        return errors;
    };

    const togglePollVisibilityModal = () => {
        setShowPollVisibilityModal(!showPollVisibilityModal);
    };

    const handleSaveSelectedUsers = (
        allEmployeesSelected,
        allClientsSelected,
        selectedUserUuids,
        employeesCountSelected,
        clientsCountSelected) => {
        setAllEmployeesInSurvey(allEmployeesSelected);
        setEmployeesCountInPoll(employeesCountSelected);

        setAllClientsInSurvey(allClientsSelected);
        setClientsCountInPoll(clientsCountSelected);

        if (allEmployeesSelected && allClientsSelected) {
            updateSurvey("userGroup", EnumUserGroup.ALL_USERS);
            setSelectedUsers([]);
            setIsShowInitialUserGroupAddButton(false);
        }
        if (allEmployeesSelected && (!allClientsSelected && clientsCountSelected === 0)) {
            updateSurvey("userGroup", EnumUserGroup.EMPLOYEES);
            setSelectedUsers([]);
            setIsShowInitialUserGroupAddButton(false);
        }
        if (allClientsSelected && (!allEmployeesSelected && employeesCountSelected === 0)) {
            updateSurvey("userGroup", EnumUserGroup.CLIENTS);
            setSelectedUsers([]);
            setIsShowInitialUserGroupAddButton(false);
        }
        if (!allEmployeesSelected && !allClientsSelected) {
            if (selectedUserUuids.length > 0) {
                setSelectedUsers(selectedUserUuids);
                updateSurvey("userGroup", EnumUserGroup.SELECTED_USERS);
                setIsShowInitialUserGroupAddButton(false);
            } else {
                setSelectedUsers([]);
                setIsShowInitialUserGroupAddButton(true);
            }
        } 

        if ((allEmployeesSelected && clientsCountSelected !== 0) || (allClientsSelected && employeesCountSelected !== 0)) {
            setSelectedUsers(selectedUserUuids);
            updateSurvey("userGroup", EnumUserGroup.SELECTED_USERS);
            setIsShowInitialUserGroupAddButton(false);
        }
        setShowPollVisibilityModal(false);
    };

    if (formStep === FormStep.PAGE_CONGRATULATIONS) {
        return (
            <PortalPageShell>
                <PageCongratulations showSurveyForm={showSurveyForm} getSurveys={getSurveys} selectedSurveyOrPollStatusType={selectedSurveyOrPollStatusType} />
            </PortalPageShell>
        );
    }

    return (
        <PortalPageShell height="calc(100vh - 64px - 1rem)" overflow="hidden" paddingBottom="0">
            <BusinessPortalHeader
                title={isEditSurveyForm ? "Edit Survey" : "Create New Survey"}
                description="Choose a uniform survey type or a multi type survey where each question can use its own response type. Target your survey to employees, clients, or both, ensuring your feedback reaches the right audience every time."
                maxWidth="94.5rem"
            />

            <PortalFormSurface maxWidth="94.5rem" marginTop="0" flex="1 1 auto" minHeight="0">
                <Form width="100%" paddingBottom="0" onSubmit={(e) => { e.preventDefault(); }} style={{ margin: 0, display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }} >
                    <PortalFormScrollBody
                        columns="minmax(0, 1fr) minmax(0, 1fr)"
                        padding="1.25rem 1.25rem 2rem"
                        gap="1rem"
                        alignItems="start"
                    >
                        <PortalFormSection padding="1rem 1rem 1.25rem">
                            <ImageUpload
                                image={formImage}
                                setFormImage={setFormImage}
                                setImageChanged={setImageChanged}
                                label="Upload Survey Background Image (Optional)"
                            />
                            <PortalFormSectionHeader withDivider>
                                <PortalFormSectionTitle>
                                    Survey Information
                                </PortalFormSectionTitle>
                                <PortalFormSectionDescription>
                                    Define the survey details, timing, audience and display rules.
                                </PortalFormSectionDescription>
                            </PortalFormSectionHeader>
                            <SurveyNameField
                                survey={survey}
                                inputSurveyName={inputSurveyName}
                                inputErrors={inputErrors}
                                loading={false}
                            />
                            <TypeOfSurveyField 
                                showSurveyForm={true}
                                survey={survey}
                                selectSurveyType={selectSurveyType}
                                inputErrors={inputErrors}
                                isEditSurveyForm={isEditSurveyForm}
                                loading={false}
                            />
                            <SurveyDurationFields
                                showSurveyForm={showSurveyForm}
                                survey={survey}
                                inputStartDate={inputStartDate}
                                inputFinishDate={inputFinishDate}
                                inputErrors={inputErrors}
                                setInputTypeAs={setInputTypeAs}
                                isEditSurveyForm={isEditSurveyForm}
                                loading={false}
                            />
                            <UserGroupField
                                showSurveyForm={true}
                                survey={survey}
                                selectUserGroup={selectUserGroup}
                                inputErrors={inputErrors}
                                isEditSurveyForm={isEditSurveyForm}
                                loading={false}
                                togglePollVisibilityModal={togglePollVisibilityModal}
                                showPollVisibilityModal={showPollVisibilityModal}
                                handleSaveSelectedUsers={handleSaveSelectedUsers}
                                selectedUsers={selectedUsers}
                                clientsInPoll={allClientsInSurvey}
                                employeesInPoll={allEmployeesInSurvey}
                                employeesCountInPoll={employeesCountInPoll}
                                clientsCountInPoll={clientsCountInPoll}
                                isEditForm={isEditSurveyForm}
                                isShowInitialUserGroupAddButton={isShowInitialUserGroupAddButton}
                            />
                            <SurveyFrequencyField
                                showSurveyForm={true}
                                survey={survey}
                                inputErrors={inputErrors}
                                selectShowIn={selectShowIn}
                                selectShowFrequency={selectShowFrequency}
                                loading={false}
                                featureFlags={user?.featureFlags}
                            />
                        </PortalFormSection>
                        <PortalFormSection padding="1rem 1rem 2rem" >
                            <PortalFormSectionHeader withDivider>
                                <PortalFormSectionTitle>
                                    Survey Questions
                                </PortalFormSectionTitle>
                                <PortalFormSectionDescription>
                                    Add the questions users will answer in the app.
                                </PortalFormSectionDescription>
                            </PortalFormSectionHeader>
                            <div>
                                <SurveyQuestionsFields
                                    survey={survey}
                                    inputQuestion={inputQuestion}
                                    selectQuestionSurveyType={selectQuestionSurveyType}
                                    addQuestion={addQuestion}
                                    removeQuestion={removeQuestion}
                                    inputErrors={inputErrors}
                                    isEditSurveyForm={isEditSurveyForm}
                                    loading={false}
                                />
                            </div>
                            <SurveySubmissionAnonymousField
                                setSurveyAnonymous={setSurveyAnonymous}
                                surveyIsAnonymous={survey.isAnonymous}
                                surveyStatus={survey.status}
                                isEditSurveyForm={isEditSurveyForm}
                                loading={false}
                            />
                        </PortalFormSection>
                    </PortalFormScrollBody>
                    <PortalFormActions sticky={false}>
                        <StandardModalActionGroup $pushRight>
                            <StandardModalActionButton type="button" onClick={(e) => { showSurveyForm(false); }} disabled={formSubmitLoading}>
                                Cancel
                            </StandardModalActionButton>
                            <StandardModalActionButton
                                type="button"
                                $variant="primary"
                                onClick={(e) => { if (isEditSurveyForm) updateFormSubmission(surveyToEdit); else createFormSubmission(); }}
                                disabled={formSubmitLoading}
                            >
                                {formSubmitLoading ? "Saving..." : "Save"}
                            </StandardModalActionButton>
                        </StandardModalActionGroup>
                    </PortalFormActions>
                </Form>
            </PortalFormSurface>
        </PortalPageShell>
    );
};


const PageCongratulations = (props) => {
    const hideFormAndReloadList = () => {
        const { showSurveyForm, getSurveys, selectedSurveyOrPollStatusType } = props;

        showSurveyForm(false);
        getSurveys(selectedSurveyOrPollStatusType);
    };

    return (
        <>
            <BusinessPortalHeader
                title="Create New Survey"
                description="Choose a uniform survey type or a multi type survey where each question can use its own response type. Direct your survey towards employees, clients, or both, ensuring you reach the right audience every time."
                maxWidth="94.5rem"
            />

            <FormBackground style={{ margin: "0 0 3rem 0",
                justifyContent: "center",
                alignItems: "center",
                display: "flex",
                flexDirection: "column",
                maxHeight: "60rem",
                flexGrow: "1",
            }}>
                <div style={{ margin: "auto" }}>
                    <Text color="#000000" size="40px" style={{ marginBlockEnd: "0px" }}>
                        Congratulations!
                    </Text>
                    <Text color="#000000" size="40px" style={{ marginBlockStart: "0px" }}>
                        Your Survey Has Been Created.
                    </Text>
                    <Text color="black" size="16px" width="650px" lineHeight="25px" margin="0">
                        You can always make edits to your scheduled and active surveys via the <br/>
                        &rsquo;Surveys&rsquo; main screen and view insights once your survey has gone live.
                    </Text>

                    <div style={{ display: "flex", justifyContent: "center", flexDirection: "row" }}>
                        <StyledButton backgroundColor="#FFF" color="#000" border="1px solid #000" style={{ width: "140px", height: "38px", marginTop: "65px", fontSize: "0.875rem" }}
                            onClick={() => { hideFormAndReloadList(); }} >
                            Close
                        </StyledButton>
                    </div>
                </div>
            </FormBackground>
        </>
    );
};

export default SurveyForm;
